import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import './Dashboard.css';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { getStudentEnrollments, getStudentGrades, getStudentPayments, data } = useData();

  const userId = user?.id || user?._id;
  const enrollments = getStudentEnrollments(userId);
  const grades = getStudentGrades(userId);
  const payments = getStudentPayments(userId);

  const activeEnrollments = enrollments.filter((enrollment) => enrollment.status === 'active');

  const enrolledCourseIds = new Set(activeEnrollments.map((enrollment) => String(enrollment.courseId || enrollment.course?.id)));
  const upcomingLessons = (data.schedules || [])
    .filter(event => enrolledCourseIds.has(String(event.courseId)))
    .filter(event => new Date(event.start) >= new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 5);

  const passedCourseIds = new Set(
    grades
      .filter((grade) => grade.status === 'passed')
      .map((grade) => String(grade.courseId || grade.course?.id))
      .filter(Boolean)
  );
  const passedGradesCount = passedCourseIds.size;
  const pendingPaymentsCount = payments.filter(p => p.status === 'pending' || p.status === 'overdue').length;
  const upcomingLessonsCount = upcomingLessons.length;

  const menuItems = [
    { key: 'courses', title: t('student.myCourses'), icon: '📚', path: '/student/courses' },
    { key: 'calendar', title: t('student.myCalendar'), icon: '📅', path: '/student/calendar' },
    { key: 'grades', title: t('student.myGrades'), icon: '📝', path: '/student/grades' },
    { key: 'homework', title: t('nav.homework'), icon: '🧠', path: '/student/homework' },
    { key: 'exams', title: t('nav.exams'), icon: '🎓', path: '/student/exams' },
    { key: 'report', title: t('student.report'), icon: '📄', path: '/student/report' },
    { key: 'payments', title: t('student.myPayments'), icon: '💳', path: '/student/payments' },
    { key: 'notifications', title: t('notification.title'), icon: '🔔', path: '/student/notifications' },
    { key: 'declarations', title: t('declaration.title'), icon: '🧾', path: '/student/declarations' }
  ];

  return (
    <div className="student-dashboard">
      <div className="dashboard-header">
        <h1>{t('student.dashboard')}</h1>
        <p className="welcome-text">{t('student.welcome', { name: user?.fullName || user?.name || '-' })}</p>
        <p className="dashboard-subtext">{t('student.dashboardHelp')}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-icon">📚</span>
          <div className="stat-info">
            <h3>{enrollments.length}</h3>
            <p>{t('student.enrolledCourses')}</p>
          </div>
        </div>

        <div className="stat-card">
          <span className="stat-icon">📝</span>
          <div className="stat-info">
            <h3>{passedGradesCount}</h3>
            <p>{t('grade.passed')}</p>
          </div>
        </div>

        <div className="stat-card">
          <span className="stat-icon">💳</span>
          <div className="stat-info">
            <h3>{pendingPaymentsCount}</h3>
            <p>{t('payment.pending')}</p>
          </div>
        </div>

        <div className="stat-card">
          <span className="stat-icon">📅</span>
          <div className="stat-info">
            <h3>{upcomingLessonsCount}</h3>
            <p>{t('student.upcomingLessons')}</p>
          </div>
        </div>
      </div>

      <div className="attention-strip">
        <div className="attention-item">
          <span className="attention-label">{t('student.pendingPayments')}</span>
          <strong>{pendingPaymentsCount}</strong>
        </div>
        <div className="attention-item">
          <span className="attention-label">{t('student.passedResults')}</span>
          <strong>{passedGradesCount}</strong>
        </div>
        <div className="attention-item">
          <span className="attention-label">{t('student.nextLessons')}</span>
          <strong>{upcomingLessons.length}</strong>
        </div>
      </div>

      <div className="dashboard-content">
        <section className="courses-section">
          <h2>{t('nav.menu')}</h2>
          <div className="courses-list">
          {menuItems.map((item) => (
            <Link key={item.key} to={item.path} className="course-card">
              <div className="course-header">
                <span className="stat-icon">{item.icon}</span>
                <span className="course-name">{item.title}</span>
              </div>
            </Link>
          ))}
          </div>
        </section>

        <section className="lessons-section">
          <h2>{t('student.upcomingLessons')}</h2>
          {upcomingLessons.length === 0 ? (
            <p className="no-data">{t('common.noData')}</p>
          ) : (
            <div className="lessons-list">
              {upcomingLessons.map((lesson) => {
                const date = new Date(lesson.start);
                const locale = i18n.language === 'nl' ? 'nl-NL' : 'en-US';
                return (
                  <div key={lesson.id} className="lesson-card">
                    <div className="lesson-date">
                      <span className="date-day">{date.getDate()}</span>
                      <span className="date-month">{date.toLocaleString(locale, { month: 'short' })}</span>
                    </div>
                    <div className="lesson-info">
                      <h4>{lesson.courseCode} - {lesson.title}</h4>
                      <p className="lesson-time">{date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</p>
                      <p className="lesson-location">{lesson.location || '-'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        </div>
      </div>
  );
};

export default Dashboard;
